import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type {
    AgenteClaveDTO,
    AseguradoraClaveResponse,
    CreateAgenteClaveRequest,
    PagaebleResult,
    SearchAseguradoraDTO,
    UpdateAgenteClaveRequest,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { clip } from '../../../utils/inputs'
import { AGENT_KEYS_PATH, errorMessage, RAMOS } from '../common/agent-key-common'
import type { AgentKeysLocationState, ComisionKey, FormMessage, RamoKey } from '../common/agent-key-common'

export interface AgentKeyFormState {
    id: number | null
    aseguradoraId: number | ''
    clave: string
    ramos: RamoKey[]
    sincronizacion: boolean
    portalClave: string
    portalPassword: string
    comisionVida: string
    comisionAutos: string
    comisionSalud: string
    comisionDiversos: string
}

const LOAD_ERROR = 'No se pudo cargar la clave de agente.'
const SAVE_ERROR = 'Error al guardar la clave de agente'
const NOT_FOUND = 'No existe la clave del agente'
const emptyState = (): AgentKeyFormState => ({
    id: null,
    aseguradoraId: '',
    clave: '',
    ramos: [],
    sincronizacion: false,
    portalClave: '',
    portalPassword: '',
    comisionVida: '0',
    comisionAutos: '0',
    comisionSalud: '0',
    comisionDiversos: '0',
})

const toFormState = (dto: AgenteClaveDTO): AgentKeyFormState => ({
    ...emptyState(),
    id: dto.id,
    aseguradoraId: dto.aseguradora?.id ?? '',
    clave: dto.clave || '',
    ramos: RAMOS.filter((ramo) => dto[ramo.id]).map((ramo) => ramo.id),
    sincronizacion: Boolean(dto.sincronizacionDalala),
    portalClave: dto.portalClave || '',
    comisionVida: String(dto.comisionVida ?? 0),
    comisionAutos: String(dto.comisionAutos ?? 0),
    comisionSalud: String(dto.comisionSalud ?? 0),
    comisionDiversos: String(dto.comisionDiversos ?? 0),
})

const percent = (value: string) => Math.min(100, Math.max(0, parseInt(value, 10) || 0))

const toRequest = (state: AgentKeyFormState, aseguradora: number): CreateAgenteClaveRequest => ({
    aseguradora,
    clave: state.clave,
    vida: state.ramos.includes('vida'),
    autos: state.ramos.includes('autos'),
    salud: state.ramos.includes('salud'),
    diversos: state.ramos.includes('diversos'),
    sincronizacionDalala: state.sincronizacion,
    portalClave: state.portalClave,
    portalPassword: state.portalPassword,
    comisionVida: percent(state.comisionVida),
    comisionAutos: percent(state.comisionAutos),
    comisionSalud: percent(state.comisionSalud),
    comisionDiversos: percent(state.comisionDiversos),
})

const sanitizePercent = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 3)
    if (!digits) return ''
    return String(Math.min(100, parseInt(digits, 10)))
}

export const useAgentKeyFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { claveId } = useParams()
    const backUrl: string = (location.state as AgentKeysLocationState | null)?.backUrl || AGENT_KEYS_PATH

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [insurers, setInsurers] = useState<SearchAseguradoraDTO[]>([])
    const [state, setState] = useState<AgentKeyFormState>(emptyState)
    const [showPassword, setShowPassword] = useState(false)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const insurersResponse: WrapperResponse<PagaebleResult<SearchAseguradoraDTO>> | null = await (await APIInvoke.invokeGET('/aseguradoras?size=99999&status=activo&order=aseguradora asc')).json()
                if (!active) return
                if (!insurersResponse?.ok) throw new Error(insurersResponse?.message || LOAD_ERROR)
                setInsurers(insurersResponse.body?.items || [])
                if (!claveId) {
                    setState(emptyState())
                    return
                }
                const keysResponse: WrapperResponse<AseguradoraClaveResponse[]> | null = await (await APIInvoke.invokeGET('/aseguradoras/claves')).json()
                if (!active) return
                if (!keysResponse?.ok) throw new Error(keysResponse?.message || LOAD_ERROR)
                const found = (keysResponse.body || [])
                    .flatMap((item) => (item.claves || []).map((clave) => ({ ...clave, aseguradora: clave.aseguradora || item.aseguradora })))
                    .find((clave) => String(clave.id) === String(claveId))
                if (!found) throw new Error(NOT_FOUND)
                setState(toFormState(found))
            } catch (error) {
                console.error(error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [claveId])

    const insurer = useMemo(() => insurers.find((item) => item.id === state.aseguradoraId) || null, [insurers, state.aseguradoraId])
    const syncAvailable = Boolean(insurer?.rpa)

    const patch = (partial: Partial<AgentKeyFormState>) => setState((current) => ({ ...current, ...partial }))

    const change = {
        aseguradoraId: (value: number | '') => {
            const next = insurers.find((item) => item.id === value)
            patch({ aseguradoraId: value, ...(next?.rpa ? {} : { sincronizacion: false }) })
        },
        clave: (value: string) => patch({ clave: clip(value.trim(), 15) }),
        toggleRamo: (value: RamoKey) => setState((current) => ({
            ...current,
            ramos: current.ramos.includes(value) ? current.ramos.filter((item) => item !== value) : [...current.ramos, value],
        })),
        sincronizacion: (value: boolean) => patch({ sincronizacion: value }),
        portalClave: (value: string) => patch({ portalClave: clip(value, 100) }),
        portalPassword: (value: string) => patch({ portalPassword: clip(value, 100) }),
        comision: (key: ComisionKey, value: string) => patch({ [key]: sanitizePercent(value) } as Partial<AgentKeyFormState>),    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!insurer) return
        try {
            setSaving(true)
            setMessage(null)
            const request = toRequest(state, insurer.id)
            const response: WrapperResponse<AgenteClaveDTO> = state.id
                ? await (await APIInvoke.invokePUT(`/aseguradoras/accounts/${state.id}`, { ...request, id: state.id } satisfies UpdateAgenteClaveRequest)).json()
                : await (await APIInvoke.invokePOST('/aseguradoras/accounts', request)).json()
            if (!response.ok || !response.body) {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
                return
            }
            history.push({ pathname: AGENT_KEYS_PATH, state: { flash: response.message || 'Clave guardada correctamente' } satisfies AgentKeysLocationState })
        } catch (error) {
            console.error('error al guardar la clave', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setSaving(false)
        }
    }

    return {
        form: state,
        insurers,
        insurer,
        syncAvailable,
        loading,
        saving,
        busy: loading || saving,
        busyLabel: loading ? 'Cargando clave…' : 'Guardando clave…',
        message,
        isEdit: Boolean(state.id),
        title: state.id ? 'Editar clave de agente' : 'Nueva clave de agente',
        backTo: backUrl,
        showPassword,
        canSave: Boolean(insurer),
        change,
        save,
        cancel: () => history.push(backUrl),
        toggleShowPassword: () => setShowPassword((value) => !value),
    }
}
